import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getVisaApplication,
  getVisaApplicationDocuments,
  uploadVisaDocument,
  getVisaRequirements
} from '../../../api/educentialApi';
import {
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  Calendar,
  Loader,
  Info
} from 'lucide-react';

const VisaDocumentChecklist = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [requirements, setRequirements] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [notification, setNotification] = useState(null);

  // Document categories based on country
  const getDocumentCategories = (country) => {
    const baseCategories = {
      UK: [
        {
          category: 'Identity Documents',
          required: true,
          documents: [
            { type: 'Passport Bio Page', required: true, hasExpiry: false },
            { type: 'Passport Full Pages', required: true, hasExpiry: false },
            { type: 'Passport Photos', required: true, hasExpiry: false }
          ]
        },
        {
          category: 'Education Documents',
          required: true,
          documents: [
            { type: 'CAS Letter', required: true, hasExpiry: false },
            { type: 'Degree Certificate', required: true, hasExpiry: false },
            { type: 'Academic Transcripts', required: true, hasExpiry: false },
            { type: 'English Proficiency Certificate', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Financial Documents',
          required: true,
          documents: [
            { type: 'Bank Statements (28 days)', required: true, hasExpiry: false },
            { type: 'Sponsor Letter', required: false, hasExpiry: false },
            { type: 'Sponsor Bank Statements', required: false, hasExpiry: false }
          ]
        },
        {
          category: 'Medical Documents',
          required: true,
          documents: [
            { type: 'TB Test Certificate', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Supporting Documents',
          required: false,
          documents: [
            { type: 'Employment Letter', required: false, hasExpiry: false },
            { type: 'Marriage Certificate', required: false, hasExpiry: false },
            { type: 'Birth Certificate', required: false, hasExpiry: false }
          ]
        }
      ],
      USA: [
        {
          category: 'Identity Documents',
          required: true,
          documents: [
            { type: 'Passport Bio Page', required: true, hasExpiry: false },
            { type: 'Passport Photos', required: true, hasExpiry: false }
          ]
        },
        {
          category: 'Education Documents',
          required: true,
          documents: [
            { type: 'I-20 Form', required: true, hasExpiry: false },
            { type: 'Degree Certificate', required: true, hasExpiry: false },
            { type: 'Academic Transcripts', required: true, hasExpiry: false },
            { type: 'Standardized Test Scores', required: false, hasExpiry: false }
          ]
        },
        {
          category: 'Financial Documents',
          required: true,
          documents: [
            { type: 'Bank Statements', required: true, hasExpiry: false },
            { type: 'Sponsor Affidavit', required: false, hasExpiry: false },
            { type: 'Tax Returns', required: false, hasExpiry: false }
          ]
        },
        {
          category: 'Visa Application',
          required: true,
          documents: [
            { type: 'DS-160 Confirmation', required: true, hasExpiry: false },
            { type: 'SEVIS Fee Receipt', required: true, hasExpiry: false },
            { type: 'Visa Fee Receipt', required: true, hasExpiry: false }
          ]
        }
      ],
      Canada: [
        {
          category: 'Identity Documents',
          required: true,
          documents: [
            { type: 'Passport Bio Page', required: true, hasExpiry: false },
            { type: 'Passport Photos', required: true, hasExpiry: false }
          ]
        },
        {
          category: 'Education Documents',
          required: true,
          documents: [
            { type: 'Letter of Acceptance', required: true, hasExpiry: false },
            { type: 'Degree Certificate', required: true, hasExpiry: false },
            { type: 'Academic Transcripts', required: true, hasExpiry: false },
            { type: 'English Test Results', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Financial Documents',
          required: true,
          documents: [
            { type: 'Proof of Funds', required: true, hasExpiry: false },
            { type: 'GIC Certificate', required: false, hasExpiry: false }
          ]
        },
        {
          category: 'Medical & Police Clearance',
          required: true,
          documents: [
            { type: 'Medical Examination', required: true, hasExpiry: true },
            { type: 'Police Clearance Certificate', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Biometrics',
          required: true,
          documents: [
            { type: 'Biometrics Confirmation', required: true, hasExpiry: false }
          ]
        }
      ],
      Australia: [
        {
          category: 'Identity Documents',
          required: true,
          documents: [
            { type: 'Passport Bio Page', required: true, hasExpiry: false },
            { type: 'Passport Photos', required: true, hasExpiry: false }
          ]
        },
        {
          category: 'Education Documents',
          required: true,
          documents: [
            { type: 'CoE (Confirmation of Enrolment)', required: true, hasExpiry: false },
            { type: 'Degree Certificate', required: true, hasExpiry: false },
            { type: 'Academic Transcripts', required: true, hasExpiry: false },
            { type: 'English Test Results', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Financial Documents',
          required: true,
          documents: [
            { type: 'Financial Capacity Evidence', required: true, hasExpiry: false },
            { type: 'OSHC (Health Insurance)', required: true, hasExpiry: true }
          ]
        },
        {
          category: 'Health & Character',
          required: true,
          documents: [
            { type: 'Health Examination', required: true, hasExpiry: true },
            { type: 'Police Clearance', required: false, hasExpiry: true }
          ]
        },
        {
          category: 'GTE Statement',
          required: true,
          documents: [
            { type: 'Genuine Temporary Entrant Statement', required: true, hasExpiry: false }
          ]
        }
      ]
    };

    return baseCategories[country] || [];
  };

  useEffect(() => {
    if (applicationId) {
      loadData();
    }
  }, [applicationId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load application
      const appResponse = await getVisaApplication(applicationId);
      if (appResponse.success) {
        setApplication(appResponse.data);

        // Load requirements
        const reqResponse = await getVisaRequirements(appResponse.data.country);
        if (reqResponse.success) {
          setRequirements(reqResponse.data);
        }
      }

      // Load uploaded documents
      const docsResponse = await getVisaApplicationDocuments(applicationId);
      if (docsResponse.success) {
        setDocuments(docsResponse.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      showNotification('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleFileSelect = async (docType, hasExpiry) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png,.doc,.docx';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        showNotification('File size must be under 10MB', 'error');
        return;
      }

      let expiryDate = null;
      if (hasExpiry) {
        const expiry = prompt('Enter expiry date (YYYY-MM-DD) if applicable:');
        if (expiry) {
          expiryDate = expiry;
        }
      }

      await handleUpload(docType, file, expiryDate);
    };
    input.click();
  };

  const handleUpload = async (docType, file, expiryDate) => {
    try {
      setUploading(true);
      setUploadingDoc(docType);

      const response = await uploadVisaDocument(applicationId, docType, file, expiryDate);

      if (response.success) {
        showNotification(`${docType} uploaded successfully`, 'success');
        loadData(); // Reload documents
      } else {
        showNotification(response.error || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showNotification(error.response?.data?.error || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      setUploadingDoc(null);
    }
  };

  const getDocumentStatus = (docType) => {
    const doc = documents.find(d => d.documentType === docType);
    if (!doc) return { status: 'not_uploaded', doc: null };

    return { status: doc.status?.toLowerCase() || 'pending', doc };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: { label: 'Verified', className: 'bg-green-100 text-green-700 border-green-200' },
      rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700 border-red-200' },
      pending: { label: 'Under Review', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
      not_uploaded: { label: 'Not Uploaded', className: 'bg-gray-100 text-gray-700 border-gray-200' }
    };

    const badge = badges[status] || badges.not_uploaded;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full border text-xs font-semibold ${badge.className}`}>
        {badge.label}
      </span>
    );
  };

  const calculateProgress = () => {
    if (!application) return 0;

    const categories = getDocumentCategories(application.country);
    const allRequiredDocs = categories
      .flatMap(cat => cat.documents)
      .filter(doc => doc.required);

    const uploadedRequired = allRequiredDocs.filter(doc => {
      const status = getDocumentStatus(doc.type);
      return status.status !== 'not_uploaded';
    });

    return Math.round((uploadedRequired.length / allRequiredDocs.length) * 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Application Not Found</h2>
          <button
            onClick={() => navigate('/visa/applications')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  const categories = getDocumentCategories(application.country);
  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-gray-50">
      {notification && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 ${
            notification.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          } text-white`}
        >
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Document Checklist</h1>
              <p className="text-gray-600 mt-1">
                {application.country} {application.visaType} - Ref: {application.applicationRef}
              </p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{progress}%</div>
              <div className="text-sm text-gray-600">Complete</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <Info className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">Document Upload Guidelines:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Upload clear, high-quality scans or photos</li>
                <li>Accepted formats: PDF, JPG, PNG, DOC, DOCX</li>
                <li>Maximum file size: 10MB per document</li>
                <li>All documents should be valid and not expired</li>
                {requirements?.biometricRequired && (
                  <li>Biometric appointment required - you'll be notified once scheduled</li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Document Categories */}
        <div className="space-y-6">
          {categories.map((category, catIndex) => (
            <div key={catIndex} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-900">
                    {category.category}
                    {category.required && <span className="text-red-600 ml-2">*</span>}
                  </h2>
                  <span className="text-sm text-gray-600">
                    {category.documents.filter(doc => {
                      const status = getDocumentStatus(doc.type);
                      return status.status !== 'not_uploaded';
                    }).length} / {category.documents.length} uploaded
                  </span>
                </div>
              </div>

              <div className="divide-y divide-gray-200">
                {category.documents.map((doc, docIndex) => {
                  const { status, doc: uploadedDoc } = getDocumentStatus(doc.type);
                  const isUploading = uploading && uploadingDoc === doc.type;

                  return (
                    <div key={docIndex} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className="flex-shrink-0">
                            {getStatusIcon(status)}
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-sm font-semibold text-gray-900">
                                {doc.type}
                              </h3>
                              {doc.required && (
                                <span className="text-xs text-red-600 font-semibold">Required</span>
                              )}
                            </div>

                            {uploadedDoc && (
                              <div className="mt-1 flex items-center space-x-4 text-xs text-gray-600">
                                <span>Uploaded: {new Date(uploadedDoc.uploadedAt).toLocaleDateString()}</span>
                                {uploadedDoc.expiryDate && (
                                  <span className="flex items-center">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    Expires: {new Date(uploadedDoc.expiryDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            )}

                            {uploadedDoc?.rejectionReason && (
                              <div className="mt-1 text-xs text-red-600">
                                Rejection reason: {uploadedDoc.rejectionReason}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center space-x-3">
                            {getStatusBadge(status)}

                            {uploadedDoc && status !== 'rejected' && (
                              <a
                                href={`${process.env.REACT_APP_API_BASE_URL || 'https://api.educential.com'}/${uploadedDoc.fileUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                            )}

                            <button
                              onClick={() => handleFileSelect(doc.type, doc.hasExpiry)}
                              disabled={isUploading || (status === 'approved')}
                              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors flex items-center ${
                                status === 'approved'
                                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : isUploading
                                  ? 'bg-gray-100 text-gray-600 cursor-wait'
                                  : status === 'rejected'
                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                  : uploadedDoc
                                  ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              {isUploading ? (
                                <>
                                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                                  Uploading...
                                </>
                              ) : status === 'approved' ? (
                                'Verified'
                              ) : uploadedDoc ? (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Replace
                                </>
                              ) : (
                                <>
                                  <Upload className="w-4 h-4 mr-2" />
                                  Upload
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="mt-8 bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Document Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {documents.length}
              </div>
              <div className="text-sm text-gray-600">Total Uploaded</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {documents.filter(d => d.status === 'APPROVED').length}
              </div>
              <div className="text-sm text-gray-600">Verified</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {documents.filter(d => d.status === 'PENDING').length}
              </div>
              <div className="text-sm text-gray-600">Under Review</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {documents.filter(d => d.status === 'REJECTED').length}
              </div>
              <div className="text-sm text-gray-600">Rejected</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaDocumentChecklist;
